import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Listens for `beforeinstallprompt` and shows a small install banner.
 * On iOS Safari (which doesn't fire that event) it shows a hint
 * to use Share → Add to Home Screen.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    const ua = window.navigator.userAgent.toLowerCase();
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const ios = /iphone|ipad|ipod/.test(ua) && !standalone;
    setIsIOS(ios);

    if (localStorage.getItem("fp_install_dismissed") === "1") setDismissed(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  if (!deferred && !isIOS) return null;

  const onInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem("fp_install_dismissed", "1");
    setDismissed(true);
  };

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-40 obs-card !p-4 flex items-start gap-3 shadow-lg"
    >
      <div className="h-10 w-10 grid place-items-center bg-blaze rounded-sm shrink-0">
        <Download className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-lg uppercase">Install Ripple Effect</div>
        {isIOS ? (
          <div className="text-xs text-zinc-400 leading-relaxed mt-1">
            Tap the <span className="font-bold">Share</span> icon then <span className="font-bold">Add to Home Screen</span> for a native app experience.
          </div>
        ) : (
          <div className="text-xs text-zinc-400 leading-relaxed mt-1">
            Add to your home screen for one-tap access and push reminders.
          </div>
        )}
        {!isIOS && (
          <button
            data-testid="install-prompt-cta"
            onClick={onInstall}
            className="blaze-btn mt-3 px-4 py-1.5 text-[11px]"
          >
            Install
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        data-testid="install-prompt-dismiss"
        className="text-zinc-500 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
